import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { makeApi, type StaffProfile } from '../api/client';

export function AdminProfilePage(): JSX.Element {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);

  const [profile, setProfile] = useState<StaffProfile | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getAdminProfile()
      .then((p) => {
        setProfile(p);
        setDisplayName(p.displayName);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load profile'));
  }, [api]);

  async function save(): Promise<void> {
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      await api.patchAdminProfile(displayName.trim());
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  }

  if (!profile && !error) return <div className="text-gray-500">Loading…</div>;

  return (
    <div className="max-w-lg space-y-6">
      <h1 className="text-xl font-semibold text-white">My Profile</h1>

      <div className="card space-y-4">
        <div>
          <label className="label">Email</label>
          <p className="text-sm text-gray-200">{profile?.email || '—'}</p>
        </div>

        <div>
          <label className="label">Role</label>
          <p className="text-sm text-gray-200 capitalize">{profile?.role || '—'}</p>
        </div>

        <div>
          <label className="label">
            Display name
            <span className="ml-1 text-gray-500 font-normal text-xs">(shown in comments)</span>
          </label>
          <input
            className="input w-full"
            type="text"
            maxLength={100}
            placeholder="e.g. Carlos Ye"
            value={displayName}
            onChange={(e) => { setDisplayName(e.target.value); setSaved(false); }}
          />
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button className="btn-primary" disabled={busy} onClick={save}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          {saved && <span className="text-sm text-emerald-400">Saved!</span>}
          {error && <span className="text-sm text-red-400">{error}</span>}
        </div>
      </div>

      <p className="text-xs text-gray-600">
        Your display name will appear on any new comments you post. Existing comments are not
        retroactively updated.
      </p>
    </div>
  );
}
